<template>
    <nav role="dialog" aria-label="Main menu" :class="['main-menu']" v-bind="$attrs">
        <div class="main-menu__grid">

        <article class="main-menu__nav main-menu__nav--dark">
            <h2 class="main-menu__nav-title" v-html="`Discover Your Purple`" />

            <ul class="main-menu__nav-list menu__nav-list">
                <li v-for="(item, i) in mainLinks" :key="i">
                    <router-link
                        v-if="item.url.startsWith('/')"
                        v-html="item.title"
                        :to="item.url" 
                        class="main-menu__nav-item text-link"
                        @click.native="innerClick()"
                    />
                    <button 
                        v-else 
                        v-html="item.title"
                        :role="!$route.path.includes('/discover-') ? 'link' : undefined"
                        :aria-current="$route.path.includes('/discover-') ? 'page' : undefined"
                        :class="['main-menu__nav-item text-link', $route.path.includes('/discover-') && `cta--${item.url}`]"
                        @click="pathClick(item.url)"
                    />
                </li>
            </ul>
            <div class="main-menu__cta has-overflow">
                <button 
                    style="border: 2px solid #fff"
                    class="button button--purple" 
                    v-html="`Learn How We Recruit`"
                    :role="!$route.path.includes('how-we-recruit') ? 'link' : undefined"
                    :aria-current="$route.path.includes('how-we-recruit') && 'page'"
                    @click="pathClick('recruit', $route.path.includes('how-we-recruit'))"
                />
            </div>
        </article>

        <article class="main-menu__nav main-menu__nav--light">
            <h2 class="main-menu__nav-title" v-html="`Doane Grant Thornton`" />
            
            <ul class="main-menu__nav-list menu__nav-list">
                <li v-for="(item, i) in menuLinks" :class="{
                    'has-submenu has-icon': hasSubmenu(item),
                    'is-active': item.submenu && submenuActive === i && isSubmenuOpen,
                }" :key="i">
                    <component 
                        :is="hasSubmenu(item) ? 'button' : 'a'"
                        class="main-menu__nav-item text-link"
                        :href="!hasSubmenu(item) && item.url"
                        :target="hasSubmenu(item) ? undefined : '_blank'"
                        :aria-expanded="hasSubmenu(item) && (submenuActive === i).toString()"
                        :aria-controls="`mainMenuToggle-${i}`"
                        :id="`mainMenuBtn-${i}`"
                        v-html="item.title"
                        @click.passive="hasSubmenu(item) ? submenuToggle($event, i) : submenuClose()"
                    />
                    <transition :name="isMobile() ? 'slide-menu' : 'fade'">
                        <ul 
                            class="main-menu__submenu submenu" 
                            :ref="`submenu-${i}`"
                            :data-link="item.title"
                            :aria-labelledby="`mainMenuBtn-${i}`"
                            @click="submenuClose()"
                            v-if="hasSubmenu(item) && submenuActive === i && isSubmenuOpen"
                        >
                            <li v-for="(item, x) in item.submenu" :key="x">
                                <a 
                                    class="main-menu__nav-item text-link"
                                    target="_blank"
                                    rel="noopener"
                                    :href="item.url"
                                    v-html="item.title"
                                />
                            </li>
                        </ul>
                    </transition>
                </li>
            </ul>

            <div class="main-menu__footer">
                <Copyright :links="false" />
                <SocialIcons :staticPosition="true" />
            </div>
        </article>

        </div>
    </nav>
</template>

<script>
import { HORIZONTAL_SCROLL_PAGES } from '@/data/pages'
import SocialIcons from './Ui/Socials'

export default {
    
    components: {
        SocialIcons,
    },
    
    props: {
        mainLinks: {
            type: [Array, Object],
            required: true
        },
        menuLinks: {
            type: [Array, Object],
            required: true
        },
    },

    data: () => ({
        submenuActive: null,
    }),
    
    computed: {
        isMenuOpen() {
            return this.$store.state.menu.opened
        },
        isSubmenuOpen() {
            return this.$store.state.menu.submenu
        },
    },

    mounted() {
        const selectors = 'a:not([tabindex="-1"]), button:not([tabindex="-1"])'
        const elems = document.querySelectorAll(selectors)

        elems.forEach((el) => {
            el.addEventListener('keydown', event => {
                if ( !this.isMenuOpen ) return;
                if ( event.keyCode !== 9 ) return;

                const { target } = event
                const [ firstEl, lastEl ] = [...elems].slice(-2)
                
                if ( Object.is(target, lastEl) && event.shiftKey ) {
                    firstEl.focus()
                    event.preventDefault()
                }
                if ( Object.is(target, firstEl) && !event.shiftKey) {
                    lastEl.focus()
                    event.preventDefault()
                }
            })
        })
    },
    
    watch: {
        isSubmenuOpen( open ) {
            if (open) {
                return;
            }

            this.delayed(() => {
                this.submenuActive = null
            })
        },
    },
    
    methods: {
        delayed: (fn, delay = 700) => {
            return setTimeout(fn, delay)
        },
        isValidUserType: (user) => {
            return ['student', 'experienced'].includes(user)
        },
        pathClick( type ) {
            const onComplete = () => this.delayed( 
                () => this.gotoUserPath(type), 0
            )

            if ( !this.isValidUserType(type) ) {
                return onComplete()
            }
            
            this.$store.dispatch('setCredentials', {
                type
            }).then(() => {
                return onComplete()
            })
            
        },
        innerClick() {
            this.delayed( 
                () => this.$emit('close', true)
            )
        },
        routeUserPath( moduleType ) {
            const { pathname: currentPath } = window.location
            const { path: destinationPath } = HORIZONTAL_SCROLL_PAGES[ moduleType ]

            // Do not route if both paths are the same
            if ( currentPath === destinationPath ) {
                return this.$root.$emit('slides-refresh', true)
            }

            const { length: currentPathLevels } = currentPath.split('/')

            const roadmapPaths = [
                '/discover-',
                '/how-we-recruit',
            ]

            // Route to opposite path and set up slides if current path is slide-related
            if ( currentPathLevels < 3 && roadmapPaths.find(el => currentPath.startsWith(el)) ) {
                
                if ( currentPath.startsWith('/discover-') && destinationPath.startsWith('/how-we-recruit') ) {
                    this.$router.push( HORIZONTAL_SCROLL_PAGES.recruit.path )
                }
                if ( currentPath.startsWith('/how-we-recruit') && destinationPath.startsWith('/discover-') ) {
                    this.$router.push( HORIZONTAL_SCROLL_PAGES.discover.path )
                }

                this.$store.dispatch(`${moduleType}/storySetup`, {
                    i: 0,
                })

                return this.$store.dispatch(`${moduleType}/slideSetup`, { 
                    i: 0, 
                    duration: .6 
                })
            }
            
            // Route to destination path if current path does not match it
            if ( currentPathLevels === 3 || !currentPath.startsWith(destinationPath) ) {
                return this.$router.push( destinationPath )
            }
        },
        async gotoUserPath( type ) {
            if ( type === 'experienced' || type === 'student' ) {
                await this.routeUserPath('discover')
            }
            if ( type === 'recruit' ) {
                await this.routeUserPath('recruit')
            }
            
            this.delayed( 
                () => {
                    this.$forceUpdate()
                    this.$emit('close', true)
                }, 600
            )
        },
        hasSubmenu( item ) {
            if ( !item || !item.submenu )
                return false
                return Object.values(item.submenu).length > 0
        },
        submenuClose() {
            this.$store.commit('setSubmenuOpened', false)
            this.submenuActive = null
            this.activeEl && this.activeEl.focus()
        },
        submenuOpen( active = 0 ) {
            this.$store.commit('setSubmenuOpened', true)
            this.submenuActive = active
            this.activeEl = document.activeElement
        },
        submenuToggle( event, active = 0 ) {
            this.$parent.activeEl = document.activeElement

            if ( this.submenuActive !== null && this.submenuActive === active ) {
                return this.submenuClose()
            }

            this.submenuOpen(active)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/logic/vars.scss';
@import '../assets/styles/logic/functions.scss';
@import '../assets/styles/logic/mixins.scss';

$queryMobile: unquote('screen and (max-width: 991px)');
$queryDesktop: unquote('screen and (min-width: 992px)');
.main-menu {
    --m-cols: 2;

    background: _c(purple-light);
    position: fixed;
    z-index: 333;
    color: #fff;
    inset: 0;
    @supports not (inset: 0) {
        top: 0; right: 0;
        left: 0; bottom: 0;
    }
}

@mixin scroll() {
    overflow-y: scroll;
    overscroll-behavior: none;
    -webkit-overflow-scrolling: touch;
}
.main-menu__grid {
    display: grid;
    width: 100%;
    height: 100dvh;

    @include scroll;
}

.main-menu__nav {
    display: flex;
    flex-direction: column;
    padding: 64px 36px;

    &--dark {
        color: #fff;
        background: _c( purple-light );
    }
    &--light {
        color: _c( purple );
        background: #fff;
    }
}

.main-menu__submenu {
    a {
        font-size: 1.1em;
    }
    a:hover {
        text-decoration: underline;
    }
}

@media #{$queryMobile} {
    .main-menu__grid {
        grid-template-rows: min-content 1fr;
    }
    .main-menu__nav--light {
        padding-bottom: 0;
    }
    .main-menu__submenu {
        display: block;
        width: 100%;
        height: 100vh;
        
        @include scroll;

        inset: 0;
        z-index: 2;
        padding: 0 36px 48px;
        background: #fff;
    }
    @media (min-width: 768px) {
        .submenu a {
            font-size: 1.25em;
        }
    }
    .main-menu__submenu::before {
        content: attr(data-link);
        display: block;
        background: inherit;
        position: sticky;
        top: 0;
        font-size: (20rem / 16);
        text-align: center;
        padding: 24px 36px;
        margin-inline: -36px;
        margin-bottom: 36px;
        border-bottom: 1px solid rgba(0,0,0, .1);
    }
    .main-menu__submenu::after {
        content: '';
        z-index: 3;
        position: fixed;

        @include arrow;

        top: 36px;
        left: 36px;
        transform: rotate(-135deg);
    }

    @media (min-width: 768px) {
        .main-menu__submenu::before {
            padding: calc(var(--space-side) + 10px) 36px;
        }
        .main-menu__submenu::after {
            top: 54px;
        }
    }
}
@media (min-width: 768px) and (max-width: 1366px) {
    .button {
        --btn-height: 64px;
        font-size: 1rem;
        padding-inline: 2.25em;
    }
}
@media #{$queryDesktop} {
    .main-menu__grid {
        grid-template-columns: repeat(var(--m-cols), 1fr);
    }
    .main-menu__nav {
        padding: 140px 116px 100px;
    }
    .main-menu__nav-list {
        position: relative;
    }
    .main-menu__submenu a {
        font-size: (21rem / 16);
    }
    .main-menu__nav--dark .menu__nav-list > li > .text-link {
        white-space: nowrap;
    }
}
@include media( 768px, 1366px ) {
    .main-menu__nav {
        padding: 100px 70px 70px;
    }
    .main-menu__submenu a {
        font-size: _f( 16 );
    }
    .privacy {
        font-size: 0.6em
    }
}
@include media( 1367px, 1680px ) {
    .main-menu__submenu a {
        font-size: _f( 19 );
    }
    .main-menu__nav {
        padding: 120px 90px 80px;
    }
}

// Active path's bullet
.main-menu__nav-list [class*='cta'] {
    position: relative;
    --i-path-size: .2em;
    --i-path-left: -.8em;
    
    &::before {
        content: '';
        backface-visibility: hidden;
        position: absolute;
        left: var(--i-path-left);
        z-index: 2;
        top: calc(50% - var(--i-path-size) / 2);
        background: var(--i-path-bg, transparent);
        border-radius: 50%;
        width: var(--i-path-size);
        height: var(--i-path-size);
        transition: _a(speed);
        transform: var(--i-path-state, scale3d(0, 0, 1));
        transition-property: background-color, transform;
    }
}
.path-student .cta--student,
.path-experienced .cta--student:hover {
    --i-path-state: scale3d(1, 1, 1);
    --i-path-bg: #{_c(coral)};
}
.path-experienced .cta--experienced,
.path-student .cta--experienced:hover {
    --i-path-state: scale3d(1, 1, 1);
    --i-path-bg: #{_c(purple-bright)};
}

// CTA Button
.main-menu__cta {
    margin-top: _f( 30 );

    .button {
        white-space: nowrap;
        letter-spacing: .1rem;
        width: 100%;
    }

    @media #{$queryDesktop} {
        margin-top: auto;
    }
}

// Menu footer
.main-menu .slide__socials {
    display: flex;
    margin-left: auto;
    margin-right: auto;
    margin-top: 10px;

    @media #{$queryDesktop} {
        margin-right: 0;
        margin-top: 0;
    }
}
.main-menu ::v-deep .social {
    path {
        fill: _c( purple-bright ) !important;
    }
    &--tiktok path:not(:first-child) {
        opacity: 0;
    }
}
.main-menu__footer {
    display: grid;
    margin-top: auto;
    padding-top: 60px;
    margin-bottom: 60px;
    position: relative;
    z-index: 0;

    .privacy {
        position: static;
        opacity: 1;
        width: auto;
    }
    .slide__socials > :nth-child(n) {
        position: static;

        ::v-deep a{ 
            --icon-size: 32px;
            position: static;
            background: none;
        }
    }
    @media #{$queryDesktop} {
        grid-template-columns: auto 1fr;
        place-items: center;
        margin-bottom: -1rem;

        .privacy {
            text-align: left;
        }
    }
    @media (min-width: 1367px) {
        .slide__socials a {
            --icon-size: 34px;
            
            ::v-deep svg { width: 16px }
        }
    }
    @media (min-width: 1681px) {
        .slide__socials > :nth-child(n) a {
            --icon-size: 44px;
            
            ::v-deep svg { width: 18px }
        }
    }
    @media (min-width: 1800px) {
        .privacy {
            font-size: 1rem;
        }
        .slide__socials > :nth-child(n) a {
            --icon-size: 52px;
        }
    }
}
</style>